import { useState, type SyntheticEvent } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Illustration, Link, Message, Spacer, Typography } from "@aviary";
import { BackButton } from "@aviary/layouts";
import { useNavigate } from "@shared/react-router-dom";
import { gRecaptchaExecute } from "@shared/utils";
import {
  useUserSendMagicLinkMutation,
  type UserSendMagicLinkMutationData,
} from "@unauthenticated/shared/data/mutations";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./SuccessContent.styles";

interface Props {
  email: string;
  setPage: (page: "email" | "success") => void;
  isSwiftFillSignIn?: boolean;
}

const SuccessContent = ({ email, setPage, isSwiftFillSignIn }: Props) => {
  const { t } = useTranslation();
  const [showResendSuccess, setShowResendSuccess] = useState<boolean>(false);
  const navigate = useNavigate();

  const onCompleted = (data: UserSendMagicLinkMutationData) => {
    const { message: errorMessage } = data?.auth?.userSendMagicLink?.errors ?? {};

    if (!errorMessage) {
      setShowResendSuccess(true);
    }
  };

  const [sendMagicLink] = useUserSendMagicLinkMutation();
  const handleSendLink = (e: SyntheticEvent) => {
    e.preventDefault();

    gRecaptchaExecute(gToken => {
      sendMagicLink({
        variables: {
          input: {
            captchaToken: gToken,
            email,
          },
        },
        onCompleted,
      });
    });
  };

  const backButton = () => {
    if (isSwiftFillSignIn) {
      return <BackButton onClick={() => navigate(-1)} text={t(l.auth.back)} isUnderLined={false} />;
    }

    return (
      <BackButton onClick={() => setPage("email")} text={t(l.auth.back)} isUnderLined={false} />
    );
  };

  return (
    <>
      {showResendSuccess && (
        <Message isColor="success">
          {t(l.signIn.continueWithoutPassword.successPage.resentSuccess)}
        </Message>
      )}

      <div css={styles.textWrapper}>
        <Typography type="h1" sizeOverride="h2">
          {t(l.signIn.checkYourInbox)}
        </Typography>
        <Spacer height="oneHalf" />
        <Illustration illustration="envelope" heightAndWidth={12} />
        <Spacer height="oneHalf" />
        <Typography type="body" sizeOverride="h4">
          <Trans
            i18nKey={l.signIn.continueWithoutPassword.successPage.emailSent}
            values={{ email }}
          >
            We sent an email to <br /> {email}
          </Trans>
        </Typography>
        <Typography>
          <Trans i18nKey={l.signIn.continueWithoutPassword.successPage.description}>
            If you have a Fullscript account, you will receive an <strong>Instant Link</strong>{" "}
            giving you access to your practitioner's Fullscript dispensary instantly. The link
            expires in 72 hours.
          </Trans>
        </Typography>
        <Spacer height="one" />
        <Typography>
          <Trans i18nKey={l.signIn.DidNotGetEmail}>
            Didn't get the email? Check your spam folder or
            <Link onClick={handleSendLink}>resend</Link>.
          </Trans>
        </Typography>
      </div>

      {backButton()}
    </>
  );
};

export { SuccessContent };
